import React, { useState, useEffect } from "react";

const FrogGame = () => {
  const [numFrogs, setNumFrogs] = useState(5);
  const [lilyPads, setLilyPads] = useState([]);
  const [selectedPadIndex, setSelectedPadIndex] = useState(null);
  const [message, setMessage] = useState("选择一片荷叶来移动上面的青蛙");
  const [gameWon, setGameWon] = useState(false);
  const [moves, setMoves] = useState(0);
  const [showRules, setShowRules] = useState(true);

  // 根据青蛙数量初始化荷叶
  useEffect(() => {
    initializeGame();
  }, [numFrogs]);

  // 每次移动后检查是否获胜
  useEffect(() => {
    checkWinCondition();
  }, [lilyPads]);

  const initializeGame = () => {
    const newLilyPads = Array(numFrogs)
      .fill(0)
      .map((_, index) => ({
        id: index,
        frogs: 1,
      }));

    setLilyPads(newLilyPads);
    setSelectedPadIndex(null);
    setMessage("选择一片荷叶来移动上面的青蛙");
    setGameWon(false);
    setMoves(0);
  };

  const checkWinCondition = () => {
    // 检查是否所有青蛙都在同一片荷叶上
    const totalFrogs = lilyPads.reduce((sum, pad) => sum + pad.frogs, 0);
    const wonPad = lilyPads.find((pad) => pad.frogs === totalFrogs);

    if (wonPad) {
      setGameWon(true);
      setMessage(
        `🎉 恭喜！所有${totalFrogs}只青蛙都跳到了同一片荷叶上，用了${moves}步！`
      );
    }
  };

  const handlePadClick = (index) => {
    if (gameWon) return;

    // 已经选择了荷叶，准备跳跃
    if (selectedPadIndex !== null) {
      // 如果点击的是同一片荷叶，取消选择
      if (selectedPadIndex === index) {
        setSelectedPadIndex(null);
        setMessage("选择一片荷叶来移动上面的青蛙");
        return;
      }

      // 执行跳跃
      jumpFrogs(selectedPadIndex, index);
    } else {
      // 选择荷叶
      if (lilyPads[index].frogs > 0) {
        setSelectedPadIndex(index);
        setMessage(
          `选择了第${index + 1}片荷叶，上面有${
            lilyPads[index].frogs
          }只青蛙，必须跳${
            lilyPads[index].frogs
          }格到有其他青蛙的荷叶上。点击目标荷叶或再次点击此荷叶取消选择。`
        );
      } else {
        setMessage("这片荷叶上没有青蛙！");
      }
    }
  };

  const jumpFrogs = (fromIndex, toIndex) => {
    const fromPad = lilyPads[fromIndex];
    const toPad = lilyPads[toIndex];
    const jumpDistance = Math.abs(toIndex - fromIndex);

    // 检查跳跃规则：几只青蛙跳几格
    if (jumpDistance !== fromPad.frogs) {
      setMessage(
        `❌ 无效的跳跃！${fromPad.frogs}只青蛙必须跳${fromPad.frogs}格，但你尝试跳${jumpDistance}格。`
      );
      setSelectedPadIndex(null);
      return;
    }

    // 新规则：不能跳到空荷叶上
    if (toPad.frogs === 0) {
      setMessage(
        `❌ 无效的跳跃！青蛙不能跳到空荷叶上，只能跳到已经有其他青蛙的荷叶上。`
      );
      setSelectedPadIndex(null);
      return;
    }

    // 执行跳跃
    const newLilyPads = [...lilyPads];
    newLilyPads[fromIndex] = { ...fromPad, frogs: 0 };
    newLilyPads[toIndex] = {
      ...newLilyPads[toIndex],
      frogs: newLilyPads[toIndex].frogs + fromPad.frogs,
    };

    setLilyPads(newLilyPads);
    setSelectedPadIndex(null);
    setMoves(moves + 1);

    const direction = toIndex > fromIndex ? "右" : "左";
    setMessage(
      `✅ ${fromPad.frogs}只青蛙从第${
        fromIndex + 1
      }片荷叶向${direction}跳了${jumpDistance}格，到了第${toIndex + 1}片荷叶`
    );

    // 检查是否获胜，如果没获胜才显示提示
    const totalFrogs = newLilyPads.reduce((sum, pad) => sum + pad.frogs, 0);
    const isWin = newLilyPads.some((pad) => pad.frogs === totalFrogs);

    // 检查荷叶上青蛙数量并提示，只有在未获胜的情况下才提示
    if (!isWin) {
      const newFrogsCount = newLilyPads[toIndex].frogs;
      if (newFrogsCount > 1) {
        setTimeout(() => {
          setMessage(
            `现在第${
              toIndex + 1
            }片荷叶上有${newFrogsCount}只青蛙，如果选择它们，必须一起跳${newFrogsCount}格到有其他青蛙的荷叶上`
          );
        }, 2000);
      }
    }
  };

  const handleNumFrogsChange = (e) => {
    const num = parseInt(e.target.value);
    if (num >= 2 && num <= 15) {
      setNumFrogs(num);
    }
  };

  const renderLilyPad = (pad, index) => {
    const isSelected = selectedPadIndex === index;
    const hasFrogs = pad.frogs > 0;

    // 计算是否是有效的跳跃目标（必须距离正确且目标荷叶上有青蛙）
    const isValidTarget =
      selectedPadIndex !== null &&
      Math.abs(index - selectedPadIndex) === lilyPads[selectedPadIndex].frogs &&
      pad.frogs > 0; // 新规则：目标荷叶必须有青蛙

    // 荷叶样式
    const padStyle = isSelected
      ? "bg-green-400 border-green-600"
      : isValidTarget
      ? "bg-yellow-200 border-yellow-500 animate-pulse"
      : hasFrogs
      ? "bg-green-100 border-green-300"
      : "bg-gray-100 border-gray-300"; // 空荷叶用灰色

    // 根据青蛙数量调整大小
    const getFrogSize = (count) => {
      if (count <= 3) return 20;
      if (count <= 6) return 16;
      if (count <= 10) return 14;
      return 12;
    };

    const frogSize = getFrogSize(pad.frogs);

    // 使用一个可爱的青蛙emoji作为临时显示
    const frogEmoji = "🐸";

    return (
      <div
        key={pad.id}
        className={`flex flex-col items-center mx-2 ${
          isSelected ? "scale-110" : ""
        }`}
        style={{ transition: "all 0.3s ease" }}
      >
        {/* 荷叶 */}
        <div
          className={`relative flex flex-col items-center justify-center cursor-pointer ${padStyle} shadow-sm hover:shadow transition-all`}
          onClick={() => handlePadClick(index)}
          style={{
            width: "80px",
            height: "80px",
            borderRadius: "50% 50% 50% 50% / 60% 60% 40% 40%",
            borderWidth: isSelected || isValidTarget ? "2px" : "1px",
            boxShadow: isSelected
              ? "0 0 12px rgba(0, 200, 0, 0.5)"
              : isValidTarget
              ? "0 0 12px rgba(255, 204, 0, 0.5)"
              : "",
            overflow: "hidden",
            opacity: hasFrogs ? 1 : 0.7, // 空荷叶半透明
          }}
        >
          {/* 荷叶纹理 */}
          <div
            className="absolute"
            style={{
              width: "80%",
              height: "80%",
              borderRadius: "50%",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              background: hasFrogs
                ? "radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(0,128,0,0.05) 70%)"
                : "radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(128,128,128,0.05) 70%)",
            }}
          ></div>

          {/* 荷叶编号 */}
          <div
            className={`absolute top-0 text-xs px-1.5 py-0.5 rounded-full font-bold shadow-sm ${
              hasFrogs ? "bg-white text-green-800" : "bg-gray-200 text-gray-600"
            }`}
          >
            {index + 1}
          </div>

          {/* 青蛙容器 - 使用emoji显示 */}
          <div className="flex flex-wrap justify-center items-center p-2 pt-4 w-full">
            {hasFrogs ? (
              <div
                className="flex flex-wrap justify-center items-center"
                style={{ maxWidth: "100%" }}
              >
                {Array(pad.frogs)
                  .fill(0)
                  .map((_, i) => (
                    <div
                      key={i}
                      className="inline-block transform hover:scale-110 transition-transform"
                      style={{
                        width: `${frogSize}px`,
                        height: `${frogSize}px`,
                        margin: "1px",
                        fontSize: `${frogSize}px`,
                        lineHeight: "1",
                        textAlign: "center",
                      }}
                    >
                      {frogEmoji}
                    </div>
                  ))}
              </div>
            ) : (
              <div className="h-12 flex items-center justify-center">
                <span className="text-gray-400 text-xs">空</span>
              </div>
            )}
          </div>
        </div>

        {/* 青蛙数量文本 */}
        <div
          className={`text-center mt-1 text-xs font-bold h-4 ${
            pad.frogs > 0 ? "text-green-800" : "text-gray-500"
          }`}
        >
          {pad.frogs > 0 ? `${pad.frogs}只` : ""}
        </div>
      </div>
    );
  };

  // 预设的游戏配置
  const presets = [
    {
      name: "简单：3只青蛙",
      setup: () => {
        const newLilyPads = Array(3)
          .fill(0)
          .map((_, index) => ({
            id: index,
            frogs: 1,
          }));
        setNumFrogs(3);
        setLilyPads(newLilyPads);
        setMessage(
          "选择一片荷叶来移动上面的青蛙。提示：先让第2只青蛙跳到第3只青蛙上，然后让第1只青蛙跳过去。"
        );
      },
    },
    {
      name: "中等：5只青蛙",
      setup: () => {
        const newLilyPads = Array(5)
          .fill(0)
          .map((_, index) => ({
            id: index,
            frogs: 1,
          }));
        setNumFrogs(5);
        setLilyPads(newLilyPads);
        setMessage("选择一片荷叶来移动上面的青蛙");
      },
    },
    {
      name: "困难：7只青蛙",
      setup: () => {
        const newLilyPads = Array(7)
          .fill(0)
          .map((_, index) => ({
            id: index,
            frogs: 1,
          }));
        setNumFrogs(7);
        setLilyPads(newLilyPads);
        setMessage("选择一片荷叶来移动上面的青蛙");
      },
    },
    {
      name: "挑战：10只青蛙",
      setup: () => {
        const newLilyPads = Array(10)
          .fill(0)
          .map((_, index) => ({
            id: index,
            frogs: 1,
          }));
        setNumFrogs(10);
        setLilyPads(newLilyPads);
        setMessage("选择一片荷叶来移动上面的青蛙");
      },
    },
  ];

  return (
    <div className="flex flex-col items-center p-4 max-w-full mx-auto bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-4 text-green-800">青蛙跳荷叶游戏</h1>

      <div className="mb-4 p-3 bg-gray-50 rounded-lg w-full shadow-sm border border-gray-100">
        <div className="flex justify-between items-center">
          <h2 className="text-md font-bold text-gray-700">游戏规则</h2>
          <button
            onClick={() => setShowRules(!showRules)}
            className="text-xs text-blue-500 hover:text-blue-700"
          >
            {showRules ? "隐藏" : "显示"}
          </button>
        </div>

        {showRules && (
          <ul className="list-disc pl-5 space-y-1 text-xs text-gray-600 mt-1">
            <li>每只青蛙初始时站在一片荷叶上</li>
            <li>荷叶上有几只青蛙，这些青蛙就必须一起跳几格</li>
            <li> 青蛙不能跳到空荷叶上，只能跳到已经有其他青蛙的荷叶上</li>
            <li>目标是让所有青蛙都跳到同一片荷叶上</li>
            <li>点击有青蛙的荷叶选择，再点击目标荷叶执行跳跃</li>
          </ul>
        )}
      </div>

      <div className="flex flex-wrap justify-between items-center w-full mb-4 bg-gray-50 p-3 rounded-lg shadow-sm">
        <div className="flex items-center mb-2 sm:mb-0">
          <label className="mr-2 font-medium text-gray-700">青蛙数量:</label>
          <input
            type="number"
            min="2"
            max="15"
            value={numFrogs}
            onChange={handleNumFrogsChange}
            className="border border-gray-300 rounded px-2 py-1 w-14 text-center shadow-sm"
          />
          <button
            onClick={initializeGame}
            className="ml-2 bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded shadow-sm text-sm"
          >
            重置
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {presets.map((preset, index) => (
            <button
              key={index}
              onClick={preset.setup}
              className={`${
                index === 0
                  ? "bg-blue-500 hover:bg-blue-600"
                  : index === 1
                  ? "bg-purple-500 hover:bg-purple-600"
                  : index === 2
                  ? "bg-red-500 hover:bg-red-600"
                  : "bg-orange-500 hover:bg-orange-600"
              } text-white px-2 py-1 rounded shadow-sm text-sm whitespace-nowrap`}
            >
              {preset.name}
            </button>
          ))}
        </div>
      </div>

      <div
        className={`w-full mb-4 p-3 rounded-lg text-center font-medium text-sm shadow-sm ${
          gameWon
            ? "bg-green-50 text-green-800 border border-green-300"
            : selectedPadIndex !== null
            ? "bg-blue-50 text-blue-800 border border-blue-300"
            : "bg-yellow-50 text-yellow-800 border border-yellow-200"
        }`}
      >
        {message}
      </div>

      {/* 可滚动的荷叶容器 - 默认居中 */}
      <div className="w-full overflow-x-auto bg-blue-50 rounded-lg mb-4 p-4 shadow-inner">
        <div
          className="flex items-end min-w-min py-2 px-2"
          style={{
            width: "max-content",
            margin: "0 auto",
            maxWidth: "100%",
          }}
        >
          {lilyPads.map(renderLilyPad)}
        </div>
      </div>

      <div className="mt-2 flex flex-col items-center">
        <p className="text-gray-600 text-sm">
          已走 <span className="text-green-600 font-bold">{moves}</span> 步
        </p>
        {gameWon && (
          <button
            onClick={initializeGame}
            className="mt-3 bg-green-500 hover:bg-green-600 text-white px-4 py-1.5 rounded shadow-sm font-medium"
          >
            再玩一次 🐸
          </button>
        )}
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default FrogGame;
